import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { FileText, Download, Mail, Eye, Trash2, X, Upload, Printer, Plus, Edit3, Pencil } from 'lucide-react';
import RemplirDocumentDialog from './RemplirDocumentDialog';
import GrilleTarifaire from './GrilleTarifaire';

export default function DocumentsSection() {
  const [documents, setDocuments] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [clients, setClients] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorDoc, setEditorDoc] = useState(null); // null = nouveau doc, sinon doc existant
  const [uploadData, setUploadData] = useState({
    nom: '',
    type: 'Légal',
    description: ''
  });
  const [emailFormData, setEmailFormData] = useState({
    recipient_email: '',
    recipient_name: '',
    recipient_type: 'custom' // 'custom' ou 'client'
  });

  useEffect(() => {
    loadDocuments();
    loadClients();
  }, []);

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
      console.log('Documents loaded:', response.data.length);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Erreur de chargement des documents');
    }
  };

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const handlePreview = (doc) => {
    // Ouvre le PDF dans le modal via iframe pour une prévisualisation intégrée fiable
    setSelectedDoc(doc);
    setShowPreviewModal(true);
  };

  const handleDownload = async (doc) => {
    try {
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${doc.fichier}`;
      // Télécharge en blob avec le nom d'origine
      const resp = await axios.get(pdfUrl, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (doc.nom || 'document').replace(/[^a-zA-Z0-9._-]/g, '_');
      a.download = safeName.endsWith('.pdf') ? safeName : `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Libérer le blob URL après un court délai pour laisser le download démarrer
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('Supprimer ce document ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/company/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenEmailModal = (doc) => {
    setSelectedDoc(doc);
    setEmailFormData({
      recipient_email: '',
      recipient_name: '',
      recipient_type: 'custom'
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailFormData.recipient_email) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const documentUrl = `${process.env.REACT_APP_BACKEND_URL}${selectedDoc.fichier}`;
      
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/company/documents/send-email`,
        {
          document_url: documentUrl,
          document_name: selectedDoc.nom,
          recipient_email: emailFormData.recipient_email,
          recipient_name: emailFormData.recipient_name || 'Client'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Document envoyé par email');
      setShowEmailModal(false);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
      console.error(error);
    }
  };

  const handleClientSelect = (clientEmail, clientName) => {
    setEmailFormData({
      ...emailFormData,
      recipient_email: clientEmail,
      recipient_name: clientName,
      recipient_type: 'client'
    });
  };

  const handlePrint = (doc) => {
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${doc.fichier}`;
    const printWindow = window.open(pdfUrl, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print();
      });
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile || !uploadData.nom || !uploadData.type) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('nom', uploadData.nom);
      formData.append('type', uploadData.type);
      formData.append('description', uploadData.description);

      console.log('Upload starting...', {
        file: uploadFile.name,
        nom: uploadData.nom,
        type: uploadData.type
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/company/documents/upload`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('Upload response:', response.data);
      toast.success('Document ajouté avec succès');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadData({ nom: '', type: 'Légal', description: '' });
      loadDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de l\'ajout du document';
      toast.error(errorMsg);
    }
  };

  const groupDocumentsByType = () => {
    const grouped = {};
    documents.forEach(doc => {
      const type = doc.type || 'Autre';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(doc);
    });
    return grouped;
  };

  const getTypeColor = (type) => {
    const colors = {
      'Tarifs': 'bg-blue-100 text-blue-700 border-blue-300',
      'Légal': 'bg-purple-100 text-purple-700 border-purple-300',
      'Business Plan': 'bg-green-100 text-green-700 border-green-300',
      'Autre': 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return colors[type] || colors['Autre'];
  };

  const groupedDocs = groupDocumentsByType();

  return (
    <div className="space-y-6">
      {/* Grille tarifaire officielle SESAM — épinglée en haut */}
      <GrilleTarifaire />

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Documents Entreprise</h2>
        <div className="flex gap-2 items-center">
          <Badge className="bg-blue-600 text-white px-3 py-1">{documents.length} document(s)</Badge>
          <Button 
            className="bg-[#1B3170] hover:bg-[#0B1E42] gap-2"
            onClick={() => { setEditorDoc(null); setShowEditorModal(true); }}
            data-testid="btn-remplir-document"
          >
            <Edit3 className="h-4 w-4" />
            Remplir un document
          </Button>
          <Button 
            variant="outline"
            className="gap-2"
            onClick={() => setShowUploadModal(true)}
            data-testid="btn-upload-document"
          >
            <Upload className="h-4 w-4" />
            Téléverser un document
          </Button>
        </div>
      </div>

      {/* Documents groupés par type */}
      {Object.keys(groupedDocs).length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="py-12 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Aucun document disponible</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedDocs).map(([type, docs]) => (
          <Card key={type} className="border-2 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  {type}
                </div>
                <Badge className={getTypeColor(type)}>{docs.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {docs.map(doc => (
                  <Card key={doc.id} className="bg-gradient-to-br from-gray-50 to-white border hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-1">{doc.nom}</h3>
                          <Badge className={`${getTypeColor(doc.type)} text-xs`}>
                            {doc.type}
                          </Badge>
                        </div>
                        <FileText className="h-8 w-8 text-blue-600" />
                      </div>

                      {doc.date_ajout && (
                        <p className="text-xs text-gray-500 mb-3">
                          Ajouté le {new Date(doc.date_ajout).toLocaleDateString('fr-FR')}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 text-blue-600 hover:bg-blue-50"
                          onClick={() => handlePreview(doc)}
                          data-testid={`btn-preview-doc-${doc.id}`}
                        >
                          <Eye className="h-4 w-4" />
                          Aperçu
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 text-cyan-600 hover:bg-cyan-50"
                          onClick={() => handleDownload(doc)}
                          data-testid={`btn-download-doc-${doc.id}`}
                        >
                          <Download className="h-4 w-4" />
                          Télécharger PDF
                        </Button>
                        {doc.source === 'editor' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="gap-1 text-indigo-600 hover:bg-indigo-50"
                            onClick={() => { setEditorDoc(doc); setShowEditorModal(true); }}
                            data-testid={`btn-edit-doc-${doc.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                            Modifier
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 text-purple-600 hover:bg-purple-50"
                          onClick={() => handleOpenEmailModal(doc)}
                        >
                          <Mail className="h-4 w-4" />
                          Envoyer
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 text-green-600 hover:bg-green-50"
                          onClick={() => handlePrint(doc)}
                        >
                          <Printer className="h-4 w-4" />
                          Imprimer
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Modal Upload Document */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-6 w-6 text-blue-600" />
              Ajouter un nouveau document
            </DialogTitle>
            <DialogDescription>
              Téléchargez un document depuis votre ordinateur
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom du document *</Label>
              <Input
                value={uploadData.nom}
                onChange={(e) => setUploadData({...uploadData, nom: e.target.value})}
                placeholder="Ex: Contrat de partenariat"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Type de document *</Label>
              <Select 
                value={uploadData.type}
                onValueChange={(value) => setUploadData({...uploadData, type: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Légal">Légal</SelectItem>
                  <SelectItem value="Tarifs">Tarifs</SelectItem>
                  <SelectItem value="Business Plan">Business Plan</SelectItem>
                  <SelectItem value="Contrat">Contrat</SelectItem>
                  <SelectItem value="Facture">Facture</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                placeholder="Description du document..."
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Fichier (PDF) *</Label>
              <div className="mt-1">
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                {uploadFile && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Fichier sélectionné: {uploadFile.name}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleUploadDocument} className="gap-2 bg-blue-600 hover:bg-blue-700">
                <Upload className="h-4 w-4" />
                Ajouter le document
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Prévisualisation */}
      {selectedDoc && (
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="doc-preview-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between pr-8">
                <span className="truncate">{selectedDoc.nom}</span>
                <div className="flex items-center gap-2">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${selectedDoc.fichier?.startsWith('/') ? '' : '/'}${selectedDoc.fichier}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                    data-testid="btn-open-preview-new-tab"
                  >
                    Ouvrir dans un nouvel onglet
                  </a>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              <iframe
                key={selectedDoc.id}
                src={`${process.env.REACT_APP_BACKEND_URL}${selectedDoc.fichier?.startsWith('/') ? '' : '/'}${selectedDoc.fichier}#toolbar=1&view=FitH`}
                className="w-full h-full border rounded-lg bg-gray-50"
                title={selectedDoc.nom}
                data-testid="doc-preview-iframe"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Envoi par email */}
      {selectedDoc && (
        <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Envoyer "{selectedDoc.nom}" par email</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Onglets Client / Email personnalisé */}
              <div className="flex gap-2 border-b">
                <button
                  className={`px-4 py-2 font-medium ${
                    emailFormData.recipient_type === 'custom'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600'
                  }`}
                  onClick={() => setEmailFormData({...emailFormData, recipient_type: 'custom'})}
                >
                  Adresse email
                </button>
                <button
                  className={`px-4 py-2 font-medium ${
                    emailFormData.recipient_type === 'client'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600'
                  }`}
                  onClick={() => setEmailFormData({...emailFormData, recipient_type: 'client'})}
                >
                  Choisir un client
                </button>
              </div>

              {emailFormData.recipient_type === 'custom' ? (
                <>
                  <div>
                    <Label>Nom du destinataire</Label>
                    <Input
                      value={emailFormData.recipient_name}
                      onChange={(e) => setEmailFormData({...emailFormData, recipient_name: e.target.value})}
                      placeholder="Ex: Jean Dupont"
                    />
                  </div>
                  <div>
                    <Label>Adresse email *</Label>
                    <Input
                      type="email"
                      value={emailFormData.recipient_email}
                      onChange={(e) => setEmailFormData({...emailFormData, recipient_email: e.target.value})}
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </>
              ) : (
                <div>
                  <Label>Sélectionner un client</Label>
                  <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg">
                    {clients.length === 0 ? (
                      <p className="p-4 text-gray-500 text-center">Aucun client disponible</p>
                    ) : (
                      <div className="divide-y">
                        {clients.map(client => (
                          <button
                            key={client.id}
                            className={`w-full text-left p-3 hover:bg-blue-50 transition-colors ${
                              emailFormData.recipient_email === client.email ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleClientSelect(client.email, client.nom)}
                          >
                            <div className="font-medium">{client.nom}</div>
                            <div className="text-sm text-gray-600">{client.email}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowEmailModal(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSendEmail} className="gap-2 bg-purple-600 hover:bg-purple-700">
                  <Mail className="h-4 w-4" />
                  Envoyer par email
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal : Remplir/Modifier un document (papier à en-tête SESAM) */}
      <RemplirDocumentDialog
        open={showEditorModal}
        onClose={() => setShowEditorModal(false)}
        existingDoc={editorDoc}
        onSaved={() => loadDocuments()}
      />
    </div>
  );
}
